import { useState } from "react";
import { CanAccess, useCanAccess, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import {
  QuoterProjectPicker,
  useQuoterProjectData,
  useSelectedQuoterProject,
} from "./quoterAdminData";
import { PriceListsEditor } from "./PriceListsEditor";
import { QuoterConfigForm } from "./QuoterConfigForm";
import { QuoterModelsEditor } from "./QuoterModelsEditor";
import { QuoterSchemesEditor } from "./QuoterSchemesEditor";
import { UnitVerificationList } from "./UnitVerificationList";

const SECTIONS = ["config", "lists", "catalogue", "verify"] as const;
type Section = (typeof SECTIONS)[number];

/**
 * Almikia Epic EQ2 (TASK-099): "Configuración del cotizador". One section at a
 * time (project configuration, price lists, models and schemes, unit
 * verification) for the project picked at the top. Almikia batch EQ3
 * (TASK-113): the gate is `quoter_configs` / `create`, held by direccion, the
 * BPO lead_manager and a lider (who only sees the projects she leads, and no
 * unit verification, which stays direccion-only). An asesora, any other
 * lead_manager and an identity without a role are sent home without anything
 * being rendered or loaded.
 */
export const QuoterConfigPage = () => (
  <CanAccess
    resource="quoter_configs"
    action="create"
    accessDenied={<Navigate to="/" replace />}
  >
    <QuoterConfigContent />
  </CanAccess>
);

QuoterConfigPage.path = "/quoter_config";

const QuoterConfigContent = () => {
  const translate = useTranslate();
  const { projects, projectId, setProjectId } = useSelectedQuoterProject(true);
  const data = useQuoterProjectData(projectId);
  const [chosenSection, setSection] = useState<Section>("config");
  // Unit verification is direccion-only (quoter_verify_units).
  const { canAccess: canVerify } = useCanAccess({
    resource: "quoter_unit_details",
    action: "edit",
  });
  const sections = SECTIONS.filter(
    (option) => option !== "verify" || canVerify,
  );
  const section = sections.includes(chosenSection) ? chosenSection : "config";

  return (
    <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <h1 className="text-xl font-semibold">
          {translate("crm.quoter.config.title")}
        </h1>
        {canVerify ? (
          <Button asChild variant="outline" className="h-11 sm:h-9">
            <Link to="/quoter_panel">{translate("crm.quoter.panel.nav")}</Link>
          </Button>
        ) : null}
      </div>

      {projects.isError ? (
        <ListErrorState
          message={translate("crm.quoter.config.load_error")}
          onRetry={() => projects.refetch()}
        />
      ) : projects.isPending ? (
        <ListCardsSkeleton rows={3} />
      ) : (projects.data ?? []).length === 0 ? (
        <ListEmptyState
          title={translate("crm.quoter.config.no_projects")}
          description={translate("crm.quoter.config.no_projects_hint")}
        />
      ) : (
        <>
          <QuoterProjectPicker
            projects={projects.data ?? []}
            value={projectId}
            onChange={setProjectId}
          />
          <nav
            className="flex flex-wrap gap-2 px-1"
            aria-label={translate("crm.quoter.config.sections")}
          >
            {sections.map((option) => (
              <Button
                key={option}
                type="button"
                variant={section === option ? "default" : "outline"}
                className="h-11 sm:h-9"
                aria-pressed={section === option}
                onClick={() => setSection(option)}
              >
                {translate(`crm.quoter.config.section_${option}`)}
              </Button>
            ))}
          </nav>

          {projectId == null ? null : section === "verify" ? (
            <UnitVerificationList projectId={projectId} />
          ) : data.isError ? (
            <ListErrorState
              message={translate("crm.quoter.config.load_error")}
              onRetry={() => data.refetch()}
            />
          ) : !data.data ? (
            <ListCardsSkeleton rows={3} />
          ) : section === "config" ? (
            <QuoterConfigForm
              key={`${data.data.config.id}-${data.data.config.updated_at}`}
              config={data.data.config}
            />
          ) : section === "lists" ? (
            <PriceListsEditor key={String(projectId)} project={data.data} />
          ) : (
            <div className="space-y-6">
              <QuoterModelsEditor project={data.data} />
              <QuoterSchemesEditor project={data.data} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
